/**
 * Which bookmaker to place a qualifying bet through.
 *
 * Moved here from @nba/domain rather than imported: the dashboard is a
 * standalone browser bundle with no workspace dependencies by design, the
 * same reason settlement-state.ts lives here.
 *
 * The best price, except where Moss rates a book as a lower limit risk.
 * Moss (31 Oct 2026): Sportsbet and PointsBet carry the same risk, so between
 * them the better price wins; Dabble is a step below both and worth a cheaper
 * price. The earlier scheme protected whichever book priced the most props,
 * which on NBA sent bets to a worse PointsBet price to spare Sportsbet.
 *
 * Two invariants hold:
 *
 *   Every option offered has already cleared the bet's minimum acceptable
 *   odds. A recommendation is never a worse bet, only a less good one.
 *
 *   A funded book wins over an unfunded one, but an unfunded one is still
 *   ranked: a thin balance is a deposit prompt, not a disqualification.
 */

export type BookAccountStatus = "ACTIVE" | "LIMITED" | "CLOSED";

export interface BookCandidate {
  readonly bookmaker: string;
  /** Decimal odds this book is offering for the bet. */
  readonly price: number;
  readonly availableCents: number;
  readonly status: BookAccountStatus;
}

export interface RoutedBook {
  readonly bookmaker: string;
  readonly price: number;
  /** What this account is short of covering the stake. Zero when funded. */
  readonly shortfallCents: number;
  /**
   * Profit forgone versus the best eligible price if the bet wins, in cents.
   * Deliberately the plain payout difference rather than a modelled EV: it is
   * the number the operator can check against the betslip.
   */
  readonly costCents: number;
  readonly eligible: boolean;
  readonly recommended: boolean;
  readonly reason: string;
}

/**
 * How much of the best price a lower-risk book may give up and still win.
 * Three percent moved 9 of 203 staked NBA bets (21-30 Oct 2026) to Dabble for
 * 0.07% of turnover: Dabble is usually either the best price or well off it.
 */
export const LOWER_RISK_GIVE_UP: Readonly<Record<string, number>> = {
  dabble: 0.03,
};

function ineligibility(
  candidate: BookCandidate,
  minimumAcceptableOdds: number,
): string | null {
  if (candidate.status === "LIMITED") return "account limited";
  if (candidate.status === "CLOSED") return "account closed";
  if (candidate.price < minimumAcceptableOdds)
    return `below the ${minimumAcceptableOdds.toFixed(2)} minimum`;
  return null;
}

/**
 * Rank the books for one bet, best choice first.
 *
 * Ineligible books are kept in the result rather than dropped, with the reason
 * they cannot be used. An empty account and a limited account look identical
 * on a betslip and mean very different things.
 */
export function routeBookmakers(
  candidates: readonly BookCandidate[],
  input: {
    readonly minimumAcceptableOdds: number;
    readonly stakeCents: number;
  },
): readonly RoutedBook[] {
  const assessed = candidates.map((candidate) => ({
    candidate,
    blocked: ineligibility(candidate, input.minimumAcceptableOdds),
    shortfallCents: Math.max(0, input.stakeCents - candidate.availableCents),
    // The price a lower-risk book competes at.
    score:
      candidate.price / (1 - (LOWER_RISK_GIVE_UP[candidate.bookmaker] ?? 0)),
  }));
  const bestPrice = assessed
    .filter((entry) => entry.blocked === null)
    .reduce((best, entry) => Math.max(best, entry.candidate.price), 0);

  const usable = assessed
    .filter((entry) => entry.blocked === null)
    .sort((left, right) => right.score - left.score);
  const winner =
    (usable.find((entry) => entry.shortfallCents === 0) ?? usable[0])?.candidate
      .bookmaker ?? null;

  const rank = (entry: (typeof assessed)[number]): number =>
    entry.candidate.bookmaker === winner ? 0 : entry.blocked === null ? 1 : 2;

  return assessed
    .sort((left, right) => rank(left) - rank(right) || right.score - left.score)
    .map(({ candidate, blocked, shortfallCents }) => {
      const giveUp =
        bestPrice > 0 ? (bestPrice - candidate.price) / bestPrice : 0;
      return {
        bookmaker: candidate.bookmaker,
        price: candidate.price,
        shortfallCents,
        costCents:
          blocked === null && bestPrice > 0
            ? Math.round(input.stakeCents * (bestPrice - candidate.price))
            : 0,
        eligible: blocked === null && shortfallCents === 0,
        recommended: candidate.bookmaker === winner,
        reason:
          blocked ??
          (shortfallCents > 0
            ? `deposit $${(shortfallCents / 100).toFixed(2)} to use`
            : giveUp <= 0
              ? "best price"
              : candidate.bookmaker === winner
                ? `lower limit risk, ${(giveUp * 100).toFixed(1)}% off the best`
                : `${(giveUp * 100).toFixed(1)}% off the best`),
      };
    });
}
